const STATUS_BY_LEGACY_ID = Object.freeze({ 1: "open", 2: "resolved", 3: "pending", 4: "expired" });

// Only reject known mismatches in already-loaded rows. The backend still owns
// discovery, message search and any dimensions absent from a partial patch.
export function matchesLoadedChatFilters(conversation, params, agentId, keptIds = null) {
  const status = conversation.lifecycle_status;
  const viewStatus = ["open", "pending", "resolved", "expired"].includes(params.view) ? params.view : null;
  if (status && viewStatus && status !== viewStatus) return false;
  if (status && params.lifecycle_status && !params.lifecycle_status.split(",").includes(status)) return false;
  const viewer = conversation.viewer_state;
  if ((params.unread || params.view === "unread") && !keptIds?.has(conversation.id) && viewer?.unread_count != null && viewer.unread_count <= 0) return false;
  if (params.view === "favourites" && viewer?.starred === false) return false;
  const assignees = params.assigned_to || (params.view === "assigned_to_me" ? "me" : params.view === "unassigned" ? "unassigned" : null);
  if (assignees && conversation.assignment && Object.prototype.hasOwnProperty.call(conversation.assignment, "assigned_to")) {
    const owner = conversation.assignment.assigned_to?.user_id;
    if (!assignees.split(",").some(value => value === "unassigned" ? owner == null : String(owner) === String(value === "me" ? agentId : value))) return false;
  }
  const activity = Date.parse(conversation.last_activity_at);
  if (Number.isFinite(activity) && ((params.last_activity_from && activity < Date.parse(params.last_activity_from))
    || (params.last_activity_to && activity > Date.parse(params.last_activity_to)))) return false;
  return true;
}

export function chatEventAffectsMembership(event, params) {
  const type = event?.type;
  if (["messaging.conversation.created", "messaging.conversation.removed", "messaging.conversation.assignment_changed", "messaging.conversation.lifecycle_changed", "messaging.conversation.participant_changed", "messaging.participant.updated"].includes(type)) return true;
  if (type === "messaging.conversation.viewer_state_changed") return Boolean(params.unread || ["unread", "favourites"].includes(params.view));
  if (type === "messaging.message.created") return params.search_scope === "message";
  if (type !== "messaging.conversation.updated") return false;
  return (event.changed_fields || []).some(path => /^(participant|assignment|lifecycle_status|business_context)(\.|$)/.test(path)
    || (path === "last_activity_at" && (params.last_activity_from || params.last_activity_to))
    || (path.startsWith("viewer_state") && (params.unread || ["unread", "favourites"].includes(params.view))));
}

function iso(value) {
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error("Choose a valid chat date range.");
  return date.toISOString();
}

/** Maps sidebar selections to the public Messaging V1 list vocabulary. */
export function canonicalChatFilterQuery({
  view, assigneeUserIds = [], statuses = [], unassigned = false,
  unread = false, queryActivity = "all", search = "", searchMode = "participant", dateRange = null,
  messageWindowEnd = new Date(),
} = {}) {
  const params = { view: view || "all" };
  if (unread) params.unread = true;
  if (unassigned) params.assigned_to = "unassigned";
  else if (assigneeUserIds.length) {
    const ids = assigneeUserIds.map(Number);
    if (ids.some(id => !Number.isSafeInteger(id) || id <= 0)) throw new Error("Agent filters require SRM user IDs.");
    params.assigned_to = [...new Set(ids)].join(",");
  }
  if (statuses.length) {
    const normalized = statuses.map(status => STATUS_BY_LEGACY_ID[Number(status)]);
    if (normalized.some(status => !status)) throw new Error("Unknown chat status filter.");
    params.lifecycle_status = [...new Set(normalized)].join(",");
  }
  if (queryActivity !== "all") params.query_activity = queryActivity;
  const term = search.trim();
  if (term) {
    params.search = term;
    if (searchMode === "message") params.search_scope = "message";
  }
  if (dateRange) {
    const [from, to] = dateRange.map(iso);
    if (from > to) throw new Error("The chat date range starts after it ends.");
    if (term && searchMode === "message") {
      params.message_from = from;
      params.message_to = to;
    } else {
      params.last_activity_from = from;
      params.last_activity_to = to;
    }
  } else if (term && searchMode === "message") {
    const end = new Date(messageWindowEnd);
    params.message_from = new Date(end.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
    params.message_to = end.toISOString();
  }
  return params;
}
